import { useEffect, useState } from "react";
import { getPOReceiptUrl, getRealFileName } from "src/Common/common";
import { can, handleError } from "src/Common/function";
import POService from "src/Services/API/po.service";
import ScannedDocumentsPanel from "../documents/ScannedDocumentsPanel";
import { DropdownMenu, KebabIcon, MenuItem } from "../common/DropdownMenu";
import {
  EditMenuIcon,
  EyeMenuIcon,
  ReceiptMenuIcon,
} from "../common/DocumentRowIcons";
import { formatMoney } from "../documents/DocumentScanModals";

const FILE_ACCEPT = "image/*,application/pdf";
const FILE_FOOTER = "Supported formats: JPG, PNG, HEIC, PDF (max. 5MB)";

// The table scrolls beyond this height (44px sticky header + 6 rows of 62px)
// instead of paginating.
const LIST_MAX_HEIGHT = "max-h-[416px]";

// PO lifecycle statuses in the same pill style as the scanned-documents
// panels (sentence case, tinted background) — hues carried over from the
// legacy po_color badges.
const PO_STATUS_PILLS = [
  { label: "Requested", className: "bg-[#E8F0FE] text-[#2E6BE6]" }, // 0
  { label: "Requested", className: "bg-[#E8F0FE] text-[#2E6BE6]" }, // 1
  { label: "Issued", className: "bg-[#FEF9C3] text-[#A16207]" }, // 2
  { label: "Purchased", className: "bg-[#F3E8FD] text-[#8E45D8]" }, // 3
  { label: "Complete", className: "bg-[#DCFCE7] text-[#15803D]" }, // 4
];

const PoStatusPill = ({ value }) => {
  const pill = PO_STATUS_PILLS[value];
  if (!pill) return null;
  return (
    <span
      className={`inline-flex px-2 py-[2px] rounded-[16px] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize ${pill.className}`}
    >
      {pill.label}
    </span>
  );
};

function Pos({
  woId,
  refreshToken,
  navigatePODetail,
  poEditHandle,
  onPreviewAttachments,
  onHasContent,
  onCount,
  onTotal,
}) {
  const [pos, setPos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Bumped after a receipt upload/confirm/delete so the PO list refetches.
  const [localRefresh, setLocalRefresh] = useState(0);
  // { id, anchor } of the row whose kebab menu is open — the anchor element
  // positions the portal-rendered dropdown.
  const [menu, setMenu] = useState(null);
  // PO whose receipts panel is expanded below its row.
  const [expandedPoId, setExpandedPoId] = useState(null);
  const isPdfFile = (fileName) =>
    fileName && fileName.toLowerCase().endsWith(".pdf");

  const canUpdatePo = can("po:update");

  useEffect(() => {
    setExpandedPoId(null);
  }, [woId]);

  useEffect(() => {
    setIsLoading(true);
    POService.getPurchaseOrdersByWoId(woId)
      .then((response) => {
        if (response.status === 200) {
          const responseData = response?.data;
          const orders = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.orders)
              ? responseData.orders
              : [];
          setPos(orders);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsLoading(false));
  }, [woId, refreshToken, localRefresh]);

  const receiptsOf = (po) =>
    (po.attachments || []).filter((attachment) => attachment?.fileName);

  // The PO's confirmed receipt total — the only amount stored for a PO
  // (mirrors the po_summary view).
  const receiptConfirmedTotal = (po) =>
    receiptsOf(po)
      .filter(
        (receipt) =>
          receipt.amountStatus === "CONFIRMED" &&
          receipt.extractedAmount != null,
      )
      .reduce((sum, receipt) => sum + Number(receipt.extractedAmount), 0);

  // Report list facts to the parent (panel highlight, header count/total).
  useEffect(() => {
    onHasContent?.(pos.length > 0);
    onCount?.(pos.length);
    onTotal?.(pos.reduce((sum, po) => sum + receiptConfirmedTotal(po), 0));
  }, [pos]);

  const refresh = () => setLocalRefresh((value) => value + 1);

  const previewReceipt = (po, index) => {
    onPreviewAttachments?.(
      receiptsOf(po).map((attachment, attachmentIndex) => ({
        id: `po-${po.id || po.number}-${attachment.id ?? attachmentIndex}`,
        fileName: attachment.fileName,
        displayName: getRealFileName(attachment.fileName),
        isPdf: isPdfFile(attachment.fileName),
        isReceipt: true,
      })),
      index,
    );
  };

  // The PO attach endpoint takes one file per request, so upload sequentially
  // and diff against the previous ids to find the rows the scan queue needs.
  const uploadReceipts = async (po, files) => {
    const previousIds = new Set(receiptsOf(po).map((receipt) => receipt.id));
    let latest = receiptsOf(po);
    for (const file of files) {
      const res = await POService.uploadFile(file, po.id);
      // /attach returns the whole PO; its attachments are the fresh list.
      latest = res?.data?.attachments || latest;
    }
    return latest.filter(
      (receipt) => receipt?.fileName && !previousIds.has(receipt.id),
    );
  };

  return (
    <>
      {!isLoading && pos.length === 0 && (
        <div className="w-full flex items-center px-6 h-[64px]">
          <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
            No purchase orders
          </span>
        </div>
      )}

      {pos.length > 0 && (
        <div className={`w-full bg-white ${LIST_MAX_HEIGHT} overflow-y-auto`}>
          {/* The header is sticky inside the same scroll container as the rows,
              so a scrollbar shrinks both alike and the columns stay aligned. */}
          <div className="w-full flex items-center h-[44px] bg-[#FBFCFF] border-b border-[#E5E8F3] sticky top-0 z-10">
            <span className="w-[140px] min-w-[90px] px-3 whitespace-nowrap font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              PO Number
            </span>
            <span className="w-[160px] grow min-w-[100px] px-3 whitespace-nowrap font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Vendor
            </span>
            <span className="w-[130px] min-w-[80px] px-3 whitespace-nowrap font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Amount
            </span>
            <span className="w-[110px] min-w-[100px] px-3 whitespace-nowrap font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Attachments
            </span>
            <span className="w-[120px] shrink-0 px-3 font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Status
            </span>
            <span className="w-[52px] shrink-0" />
          </div>

          {pos.map((po) => {
            const receipts = receiptsOf(po);
            const confirmedTotal = receiptConfirmedTotal(po);
            const isExpanded = expandedPoId === po.id;
            return (
              <div
                key={po.id}
                className="w-full border-b border-[#E5E8F3] last:border-b-0"
              >
                <div className="w-full flex items-center h-[62px]">
                  <span className="w-[140px] min-w-[90px] px-3">
                    <a
                      href={`/purchase/${po.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#0065FD] cursor-pointer truncate block"
                      title={po.number}
                    >
                      {po.number}
                    </a>
                  </span>

                  <span className="w-[160px] grow min-w-[100px] px-3 flex flex-col">
                    <span
                      className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477] truncate"
                      title={po.vendor}
                    >
                      {po.vendor}
                    </span>
                    {po.issuedUser?.name && (
                      <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] truncate">
                        Issued for {po.issuedUser.name}
                      </span>
                    )}
                  </span>

                  <span className="w-[130px] min-w-[80px] px-3 flex">
                    {confirmedTotal > 0 ? (
                      <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332]">
                        {formatMoney(confirmedTotal)}
                      </span>
                    ) : (
                      <span className="font-InterRegular text-[12px] leading-[16px] text-[#99A1AF]">
                        —
                      </span>
                    )}
                  </span>

                  <span className="w-[110px] min-w-[100px] px-3 flex">
                    {receipts.length > 0 ? (
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedPoId(isExpanded ? null : po.id)
                        }
                        className="whitespace-nowrap font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#0065FD] cursor-pointer"
                      >
                        {receipts.length} receipt
                        {receipts.length > 1 ? "s" : ""}
                      </button>
                    ) : (
                      <span className="font-InterRegular text-[12px] leading-[16px] text-[#99A1AF]">
                        —
                      </span>
                    )}
                  </span>

                  <span className="w-[120px] shrink-0 px-3">
                    <PoStatusPill value={po.status} />
                  </span>

                  <span className="w-[52px] shrink-0 pr-3 flex justify-end">
                    <button
                      type="button"
                      onClick={(e) =>
                        setMenu(
                          menu?.id === po.id
                            ? null
                            : { id: po.id, anchor: e.currentTarget },
                        )
                      }
                      className="p-[10px] rounded-[8px] text-[#5F6477] hover:bg-[#F1F3FA] cursor-pointer"
                      aria-label="Purchase order actions"
                    >
                      <KebabIcon />
                    </button>
                    {menu?.id === po.id && (
                      <DropdownMenu
                        anchorEl={menu.anchor}
                        onClose={() => setMenu(null)}
                      >
                        <MenuItem
                          icon={<EyeMenuIcon />}
                          onClick={() => {
                            setMenu(null);
                            navigatePODetail(po);
                          }}
                        >
                          View Details
                        </MenuItem>
                        {canUpdatePo && (
                          <MenuItem
                            icon={<EditMenuIcon />}
                            onClick={() => {
                              setMenu(null);
                              poEditHandle(po);
                            }}
                          >
                            Edit
                          </MenuItem>
                        )}
                        <MenuItem
                          icon={<ReceiptMenuIcon />}
                          onClick={() => {
                            setMenu(null);
                            setExpandedPoId(isExpanded ? null : po.id);
                          }}
                        >
                          {isExpanded ? "Hide Receipts" : "Receipts"}
                        </MenuItem>
                      </DropdownMenu>
                    )}
                  </span>
                </div>

                {/* Expanded receipts — the full ScannedDocumentsPanel card,
                      identical to the WO Proposals/Invoices sections. */}
                {isExpanded && (
                  <div className="w-full px-6 pb-4">
                    <ScannedDocumentsPanel
                      title="Receipts"
                      noun="Receipt"
                      totalLabel="Purchase Order's total receipted amount"
                      documents={receipts}
                      canManage={canUpdatePo}
                      // The receipt rows' kebab only offers Edit Amount.
                      canDelete={false}
                      allowDownload={false}
                      accept={FILE_ACCEPT}
                      footer={FILE_FOOTER}
                      allowRename={false}
                      getFileUrl={(receipt) =>
                        getPOReceiptUrl(receipt.fileName)
                      }
                      uploadFn={(files) => uploadReceipts(po, files)}
                      scanFn={(id) => POService.scanReceipt(id)}
                      confirmFn={(id, amount, sourcePage, sourceLabel) =>
                        POService.confirmReceiptAmount(
                          id,
                          amount,
                          sourcePage,
                          sourceLabel,
                        )
                      }
                      deleteFn={(id) => POService.deleteAttachment(id)}
                      onRefresh={refresh}
                      onView={(receipt) => {
                        const receiptIndex = receipts.findIndex(
                          (entry) => entry.id === receipt.id,
                        );
                        previewReceipt(
                          po,
                          receiptIndex >= 0 ? receiptIndex : 0,
                        );
                      }}
                      emptyText="No receipts"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

export default Pos;
